import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiService } from '../services/api';
import type { ChildrenListResponse } from '../types/api';
import type { FileSortField, FileSortOrder } from './Dashboard/types/dashboard.types';
import type { UploadError } from './file-uploader/uploadResults';
import { uploadParentId } from './file-browser/childrenModel';
import { isClientError } from './file-browser/folderLoadFailure';
import type { FolderRef } from './file-browser/types';

// STD-015: server state for the File Management domain.
// apiService is imported here (a *.queries.ts file) per STD-016.
// Errors are surfaced by the component inline (setError + showError) so global
// toasts are skipped. The loadFiles read uses useQuery; write operations use
// useMutation with list-key invalidation.

// ---------------------------------------------------------------------------
// Key factory
// ---------------------------------------------------------------------------

export interface FolderListParams {
  /** The node to list. `null` = the caller's own organization root. */
  nodeId: string | null;
  selectedSerial?: string;
  startDate?: string;
  endDate?: string;
  includeUntagged?: boolean;
  sortBy?: FileSortField;
  sortOrder?: FileSortOrder;
}

export const fileManagementKeys = {
  all: ['files'] as const,
  /**
   * Keyed on the NODE, per §4's "navigation cache keyed by node id". A path key
   * went stale the moment anything above the folder was renamed — the id does
   * not, because a rename is one row UPDATE and the id is what does not change
   * (M-7).
   */
  folder: (p: FolderListParams) => [...fileManagementKeys.all, 'folder', p] as const,
};

// ---------------------------------------------------------------------------
// Read — the ONE navigation contract (STD-029 R-8, B-4)
// ---------------------------------------------------------------------------
//
// `useFileListQuery` is gone with `apiService.browseFiles`. It fetched the flat,
// recursive, path-keyed projection of the caller's whole tree and left the
// browser to synthesise folders from key segments, derive their counts, and sort
// — none of which could describe a folder another organization shared, because a
// path is a coordinate inside one organization's own tree (D-6).
//
// The endpoint itself is permanent and untouched: `GET /api/files/browse` is
// MissionPlanner's folder picker (D-4b). What retired is the dashboard's use of
// it (D-5).

/**
 * One folder's children, plus its ancestor chain and the totals.
 *
 * `sortBy`/`sortOrder` are **server** parameters now. They have to be: the page
 * is bounded, folder rows sort on aggregates only the server can compute, and
 * sorting a truncated page on the client is a lie about which rows you were
 * given.
 */
export function useFolderChildrenQuery(
  params: FolderListParams,
  /**
   * `enabled: false` withholds the request entirely. Used for one case: the url
   * named something that cannot be a row id, so `nodeId` fell back to `null` and
   * the query would otherwise fetch the caller's OWN ROOT in the background
   * while the dead-link panel is what renders. Nothing consumes that response —
   * it is a request made to be discarded.
   */
  options: { enabled?: boolean } = {}
) {
  return useQuery<ChildrenListResponse, Error>({
    queryKey: fileManagementKeys.folder(params),
    enabled: options.enabled ?? true,
    // Errors are shown inline by the component.
    meta: { skipGlobalErrorToast: true },
    // A 4xx here is a refusal the server will repeat identically — the node is
    // gone, invisible, or is a file (B-5). Retrying it costs the user a delay
    // they read as "the app is still looking for my folder" before the dead-link
    // panel finally appears. Anything 5xx or connection-shaped keeps the global
    // `retry: 1`, because those genuinely can differ on a second attempt.
    retry: (failureCount, error) => !isClientError(error) && failureCount < 1,
    queryFn: () =>
      apiService.listChildren(params.nodeId, {
        serialNumber: params.selectedSerial,
        startDate: params.startDate,
        endDate: params.endDate,
        includeUntagged: params.includeUntagged,
        sortBy: params.sortBy,
        sortOrder: params.sortOrder,
      }),
  });
}

// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------

export interface UploadFileMetadata {
  destinationPath: string;
  flightType: string;
  payloadId: number;
  pilotId: string;
  customerId: string;
  projectId: string;
  airspaceObserverId: string;
  payloadOperatorId: string;
}

/** Where an upload batch is going: the open folder, and whether it is somebody else's. */
export interface UploadTarget {
  insideShare: boolean;
  currentFolder: FolderRef;
}

/**
 * One upload, to one of two endpoints — `uploadParentId` decides which (#1351).
 *
 * Inside a folder another organization shared the upload goes to the
 * id-addressed `POST /api/file-objects/{parent_id}/upload`: a path is a
 * coordinate in the caller's own tree, and a shared folder has no spelling
 * there. In the caller's own tree the path-addressed `POST /api/files/upload`
 * (MissionPlanner's lane, STD-029 D-4b) stays the SPA's route — its root has no
 * id to address. Both run the same flight ingest, so the result shape is the
 * same to the uploader. The unreachable "foreign folder with no id" answer is
 * refused as an ordinary upload failure, never let through to the path
 * endpoint where it would misfile the file into the caller's own tree.
 */
export function useUploadFileMutation() {
  return useMutation<
    | Awaited<ReturnType<typeof apiService.uploadFile>>
    | Awaited<ReturnType<typeof apiService.uploadIntoObject>>,
    UploadError,
    { file: File; metadata: UploadFileMetadata; target: UploadTarget }
  >({
    meta: { skipGlobalErrorToast: true },
    mutationFn: async ({ file, metadata, target }) => {
      try {
        const parentId = uploadParentId(target.insideShare, target.currentFolder);
        if (parentId === null) {
          const unaddressable: UploadError = new Error(
            'This folder cannot be uploaded into from here. Open it again from the Shared view.'
          );
          unaddressable.errorCode = 'UPLOAD_TARGET_UNADDRESSABLE';
          throw unaddressable;
        }
        return parentId
          ? await apiService.uploadIntoObject(parentId, file, metadata)
          : await apiService.uploadFile(file, metadata);
      } catch (err) {
        if (err instanceof Error) {
          (err as Error & { fileName: string }).fileName = file.name;
        }
        throw err;
      }
    },
  });
}

export function useAnalyzeFileMutation() {
  return useMutation<
    Awaited<ReturnType<typeof apiService.analyzeFile>>,
    Error,
    { file: File; flightType?: string }
  >({
    meta: { skipGlobalErrorToast: true },
    mutationFn: ({ file, flightType }) => apiService.analyzeFile(file, flightType),
  });
}

/**
 * Presign a download for one entry.
 *
 * **Id-addressed whenever the entry has a row**, which since B-4 is the case
 * that matters: `GET /api/files/{path}/download` asks "is this path inside the
 * caller's own org prefix?", and a file inside a folder somebody shared can
 * never pass that — its path is relative to the share root and names nothing in
 * the caller's tree. `GET /api/file-objects/{id}/download` asks the database
 * whether the caller may SELECT the row, which is the same ancestor walk that
 * authorised the share.
 *
 * The path arm survives for R-9 entries, which have no row to address. That is
 * safe by construction: an unregistered flight only ever appears in its own
 * organization's listing, so its key is always in the caller's own tree.
 */
export function useGetDownloadUrlMutation() {
  return useMutation<{ download_url?: string }, Error, { objectId?: string; fileKey: string }>({
    meta: { skipGlobalErrorToast: true },
    mutationFn: ({ objectId, fileKey }) =>
      objectId ? apiService.getObjectDownloadUrl(objectId) : apiService.getDownloadUrl(fileKey),
  });
}

// ---------------------------------------------------------------------------
// Writes — id-addressed since STD-029 B-3
// ---------------------------------------------------------------------------
//
// Every mutation below names a `fleet.file_objects` row. They used to send
// display paths, which meant they could only ever act inside the caller's own
// tree: a folder another organization shared has no spelling there, so a `write`
// share was inert on this surface (D-4).
//
// **Invalidation widened to the whole domain in B-4, and that is a bug fix.**
// It used to invalidate one list key — the folder being looked at. That was
// survivable while the listing was RECURSIVE (one key held the whole tree, so
// every affected folder was in it) and became wrong the moment a listing is one
// level: a move changes TWO folders, the source and the destination, and only
// one of them is the key we hold. The other stayed stale until something else
// refetched it. There is no per-folder invalidation to be clever with here,
// because the client does not know the destination's ancestors.

export interface DeleteObjectInput {
  objectId: string;
  /** Folders only; a file ignores it. The browser always deletes recursively. */
  recursive?: boolean;
}

export function useDeleteObjectMutation() {
  const queryClient = useQueryClient();
  return useMutation<Awaited<ReturnType<typeof apiService.deleteObject>>, Error, DeleteObjectInput>(
    {
      meta: { skipGlobalErrorToast: true },
      mutationFn: ({ objectId, recursive }: DeleteObjectInput) =>
        apiService.deleteObject(objectId, { recursive }),
      onSuccess: () => queryClient.invalidateQueries({ queryKey: fileManagementKeys.all }),
    }
  );
}

export interface MoveItemInput {
  objectId: string;
  /** `null` = the caller's own organization root. See `parentIdFor`. */
  parentId: string | null;
}

export function useMoveItemMutation() {
  const queryClient = useQueryClient();
  return useMutation<Awaited<ReturnType<typeof apiService.updateObject>>, Error, MoveItemInput>({
    meta: { skipGlobalErrorToast: true },
    // No `isFolder` branch any more, and its absence is the point: a folder and
    // a file move by the same single row UPDATE, because the bytes never move
    // (M-2/M-7). The two endpoints that used to be needed here existed only
    // because one of them had to paginate a copy of an S3 prefix.
    mutationFn: ({ objectId, parentId }: MoveItemInput) =>
      apiService.updateObject(objectId, { parentId }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: fileManagementKeys.all }),
  });
}

export interface RenameObjectInput {
  objectId: string;
  newName: string;
}

export function useRenameObjectMutation() {
  const queryClient = useQueryClient();
  return useMutation<Awaited<ReturnType<typeof apiService.updateObject>>, Error, RenameObjectInput>(
    {
      meta: { skipGlobalErrorToast: true },
      mutationFn: ({ objectId, newName }: RenameObjectInput) =>
        apiService.updateObject(objectId, { name: newName }),
      onSuccess: () => queryClient.invalidateQueries({ queryKey: fileManagementKeys.all }),
    }
  );
}

export interface CreateFolderInput {
  /** `null` = the caller's own organization root. */
  parentId: string | null;
  name: string;
}

export function useCreateFolderMutation() {
  const queryClient = useQueryClient();
  return useMutation<
    Awaited<ReturnType<typeof apiService.createFolderObject>>,
    Error,
    CreateFolderInput
  >({
    meta: { skipGlobalErrorToast: true },
    mutationFn: ({ parentId, name }: CreateFolderInput) =>
      apiService.createFolderObject({ parentId, name }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: fileManagementKeys.all }),
  });
}
